import type { IAppSummary } from '~/types/application'
import type { IAppManagement, IAppManagementByDeviceType } from '~/types/application-management'
import type { DeviceType } from '~/types/device-type'

import { DEVICE_TYPE } from '~/const'

export const getHeaderParams = (
  path: string
): {
  backNavigation?: {
    title: string
    to: string
  }
  title: string
} | null => {
  const includeInUrl = (value: string, element: string): string => {
    if (
      (value.includes('favourites') && element === 'favourites') ||
      (value.includes('business-report/legacy') && element === 'business-report/legacy') ||
      (value.includes('business-report/overview') && element === 'business-report/overview') ||
      (value.includes('business-report/sandbox') && element === 'business-report/sandbox') ||
      (value.includes('aso/advanced') && element === 'aso/advanced') ||
      (value.includes('aso/overview') && element === 'aso/overview') ||
      (value.includes('ca/overview') && element === 'ca/overview') ||
      (value.includes('ca/metrics-overview') && element === 'ca/metrics-overview') ||
      (value.includes('ca/publishing-overview') && element === 'ca/publishing-overview') ||
      (value.includes('ca/monet-ads') && element === 'ca/monet-ads') ||
      (value.includes('ca/monet-iap') && element === 'ca/monet-iap') ||
      (value.includes('ca/stability') && element === 'ca/stability') ||
      (value.includes('ca/usage') && element === 'ca/usage') ||
      (value.includes('ca/app-usage') && element === 'ca/app-usage') ||
      (value.includes('ca/community') && element === 'ca/community') ||
      (value.includes('events') && element === 'events') ||
      (value.includes('builds') && element === 'builds') ||
      (value.includes('checklists') && element === 'checklists' && path.split('/').length > 4) ||
      (value.includes('external-links') && element === 'external-links') ||
      (value.includes('in-app-purchases') && element === 'in-app-purchases') ||
      (value.includes('ranking') && element === 'ranking') ||
      (value.includes('versions') && element === 'versions') ||
      (value.includes('app-config') && element === 'app-config')
    ) {
      return path
    }

    return ''
  }

  switch (path) {
    case includeInUrl(path, 'favourites'):
      return { title: 'Favourites data' }
    case includeInUrl(path, 'business-report/legacy'):
      return { title: 'Business Reports - Reporting' }
    case includeInUrl(path, 'business-report/overview'):
      return { title: 'Business Reports - Overview' }
    case includeInUrl(path, 'business-report/sandbox'):
      return { title: 'Business Reports - Sandbox' }
    case includeInUrl(path, 'aso/advanced'):
      return { title: 'ASO advanced' }
    case includeInUrl(path, 'aso/overview'):
      return { title: 'ASO overview' }
    case includeInUrl(path, 'ca/overview'):
      return { title: 'Cohorted Analytics - Overview' }
    case includeInUrl(path, 'ca/metrics-overview'):
      return { title: 'Cohorted Analytics - Overview Metrics' }
    case includeInUrl(path, 'ca/publishing-overview'):
      return { title: 'Cohorted Analytics - Overview Publishing' }
    case includeInUrl(path, 'ca/monet-ads'):
      return { title: 'Cohorted Analytics - Monet Ads' }
    case includeInUrl(path, 'ca/monet-iap'):
      return { title: 'Cohorted Analytics - Monet IAP' }
    case includeInUrl(path, 'ca/stability'):
      return { title: 'Cohorted Analytics - Stability' }
    case includeInUrl(path, 'ca/usage'):
      return { title: 'Cohorted Analytics - Usage' }
    case includeInUrl(path, 'ca/app-usage'):
      return { title: 'Cohorted Analytics - App Usage' }
    case includeInUrl(path, 'ca/community'):
      return { title: 'Cohorted Analytics - Community' }
    case includeInUrl(path, 'external-links'):
      return { title: 'App external links' }
    case includeInUrl(path, 'events'):
      return { title: 'App events' }
    case includeInUrl(path, 'in-app-purchases'):
      return { title: 'In-App-Purchases' }
    case includeInUrl(path, 'ranking'):
      return { title: 'App ranking' }
    case `/checklists`:
      return { title: 'App to-do lists' }
    case includeInUrl(path, 'versions'):
      return { title: 'App versions' }
    case includeInUrl(path, 'builds'):
      return { title: 'App builds' }
    case includeInUrl(path, 'app-config'):
      return { title: 'Social login config' }
    default:
      return null
  }
}

export const getAppSummaryFromAppKey = ({
  appKey,
  apps,
  deviceType,
}: {
  appKey?: string
  apps: IAppSummary[]
  deviceType?: DeviceType
}): IAppSummary[] => {
  if (apps && appKey && deviceType) {
    const result = apps.find((app) => app.app_key === appKey && app.device_type === deviceType)
    return result ? [result] : []
  }
  return []
}

export const appKeyDeviceTypeToString = (appKey: string, deviceType: string): string => `${appKey}:${deviceType}`

export const getStoreLabelFromDeviceType = (deviceType: DeviceType): string => {
  switch (deviceType) {
    case DEVICE_TYPE.ANDROID:
      return 'Play Store'
    case DEVICE_TYPE.IOS:
      return 'App Store'
    default:
      return ''
  }
}

export const getExternalStoreId = (application: IAppManagement, deviceType: string): string | undefined => {
  switch (deviceType) {
    case 'ios':
      // NOTE: the store_id is a number, but we need to return a string
      return application.ios ? `${application.ios.store_id}` : undefined
    case 'android':
      return application.android ? application.android.bundle_id : undefined
    default:
      return undefined
  }
}

export const getExternalStoreLink = (application: IAppManagement, deviceType: string): string | undefined => {
  switch (deviceType) {
    case 'ios':
      // NOTE: the store_id is a number, but we need to return a string
      return application.ios ? `http://itunes.apple.com/app/id${application.ios.store_id}` : undefined
    case 'android':
      return application.android
        ? `https://play.google.com/store/apps/details?id=${application.android.bundle_id}`
        : undefined
    default:
      return undefined
  }
}

export const splitByDeviceType =
  ({ withWeb = true }: { withWeb?: boolean } = {}) =>
  (
    newArray: IAppManagementByDeviceType[],
    { android, ios, web, ...rest }: IAppManagement
  ): IAppManagementByDeviceType[] => {
    if (android) {
      newArray.push({ ...rest, device_type: 'android', external_store_id: android.private_store_id })
    }
    if (ios) {
      newArray.push({ ...rest, device_type: 'ios', external_store_id: `${ios.store_id}` })
    }
    if (web && withWeb) {
      newArray.push({ ...rest, device_type: 'web' })
    }

    return newArray
  }

export const transformByDeviceType = (
  { android, ios, ...rest }: IAppManagement,
  deviceType: DeviceType
): IAppManagementByDeviceType => {
  switch (deviceType) {
    case 'android':
      return { ...rest, device_type: 'android', external_store_id: android ? android.private_store_id : '' }
    case 'ios':
      return { ...rest, device_type: 'ios', external_store_id: ios ? `${ios.store_id}` : '' }
    case 'web':
      return { ...rest, device_type: 'web' }
    default:
      throw new Error('Invalid device type')
  }
}
